import Button from '@/components/Button';
import { GameSummary } from '@/components/GameSummary';
import type { GameProps } from '@/components/Gameinterfaces';
import { Icon, IconVariant } from '@/components/Icon';
import OfflineInfo from '@/components/OfflineInfo';
import StickyBox from '@/components/StickyBox';
import { SummaryPrice } from '@/components/SummaryPrice';
import { eurojackpot } from '@/config/game/eurojackpot';
import { gluecksSpirale } from '@/config/game/gluecksSpirale';
import { GameStep } from '@/config/game/steps';
import MarketingParams from '@/helpers/MarketingParams';
import classnames from '@/utilities/classnames';
import isOffline from '@/utilities/isOffline';
import isStandaloneMode from '@/utilities/isStandaloneMode';
import pushDatalayer, { DataLayerEvent } from '@/utilities/pushDatalayer';
import { findSubsidiaryByKey } from '@/utilities/subsidiaries';
import { useSearchParams } from 'next/navigation';
import React, { useEffect } from 'react';

interface GameStepSummaryProps extends GameProps {
  [key: string]: any;
}
const GameStepSummary: React.FC<
  React.PropsWithChildren<GameStepSummaryProps>
> = (props) => {
  const iframe = props.iframe;
  const externalGluecksSpirale =
    props.id === gluecksSpirale.id && props.external;

  const externalEuroJackpot = props.id === eurojackpot.id && props.external;
  const searchParams = useSearchParams();
  const searchTipps = searchParams.get('st');
  const search = searchParams.get('s');

  useEffect(() => {
    // URL-Parameter "s(ubsidiary)" bei IFrame? Extra behandeln!
    async function searchForSubsidiary(search: string) {
      const sub = findSubsidiaryByKey(search);
      if (sub) {
        const currentSubsidiary = localStorage?.getItem('currentSubsidiary');
        if (currentSubsidiary === sub.id) {
          return;
        }
        localStorage?.setItem('currentSubsidiary', sub.id);
        const params = new URLSearchParams(searchParams.toString());
        window.history.replaceState({}, '', `?${params.toString()}`);
        window.location.reload();
      }
    }
    if (iframe && search && searchTipps === '1') {
      searchForSubsidiary(search);
    }
  }, [searchTipps, search, iframe, searchParams]);

  const handleOnSubmit = ($event) => {
    $event.preventDefault();

    if (isStandaloneMode()) {
      if (isOffline()) {
        $event.stopPropagation();
        OfflineInfo.open();
      } else {
        const url = 'http://' + window.location.host + props.loginPagePath;
        const serializedGamesState = JSON.stringify(props.allState);
        const urlWithState = `${url}?__gamedata=${serializedGamesState}`;
        props.onResetGameState(props.id.toString());
        window.open(urlWithState, '_blank');
      }
    } else {
      props.handleNextStep();
    }
  };

  return (
    <div className="h-flex-gm h-flex-gm--cols">
      <div className="h-flex__col h-flex__col--6-10">
        <div className="Game__groupContainer gs-summary">
          {externalEuroJackpot && (
            <h1 className="Step__header">Zusammenfassung</h1>
          )}

          {externalGluecksSpirale && (
            <>
              <h1 className="Step__header">Dein Spieltipp</h1>
              <p className="Step__intro">
                Für die Abgabe bei Deiner Landeslotteriegesellschaft
              </p>
            </>
          )}

          {!externalGluecksSpirale && !externalEuroJackpot && (
            <>
              <h1 className="Step__title">{props.title} spielen</h1>
              <h2 className="Step__header">
                Ihr Spieltipp für die Abgabe bei Ihrer
                Landeslotteriegesellschaft
              </h2>
            </>
          )}

          <GameSummary
            currentGame={props}
            selectedNumbers={props.crossedNumbers}
            selectedPlayNumber={props.selectedPlayNumber}
            selectedAdditionalGameIds={props.selectedAdditionalGames}
            selectedWeeksNumber={props.selectedWeeksNumber}
            selectedDrawDays={props.selectedDrawDays}
            selectedDrawNumber={props.selectedDrawNumber}
            selectedDrawRate={props.selectedDrawRate}
            superNumberIndex={
              (props.playNumber && props.playNumber.superNumberIndex) || -1
            }
            gameShortName={props.shortname}
            hasDrawNumbers={!!props.drawNumbers && props.drawNumbers.length > 0}
            hasDrawRate={!!props.drawRate && props.drawRate.length > 0}
            drawTypes={props.drawTypes}
            weeksNumber={props.weeksNumber}
            onWeeksNumberChange={(value) => {
              props.onWeeksNumberUpdate(props.id, value);
              pushDatalayer(
                `click_element_name`,
                `Anzahl der Wochen - ${value}`,
                DataLayerEvent.UserClick
              );
            }}
            hasWeeksNumberSelection={
              props.steps.indexOf(GameStep.Details) === -1
            }
            noHidePlayNumber={props.playNumber && props.playNumber.noHide}
            additionalGames={props.additionalGames}
            selectedWeeklySubscription={props.selectedWeeklySubscription}
            couponGroupsConfig={props.couponGroup}
          />
        </div>
      </div>
      <div className="h-flex__col h-flex__col--4-10">
        <StickyBox>
          <form
            method="get"
            action={window.location && props.loginPagePath}
            className="h-spacer-medium-bottom"
            onSubmit={handleOnSubmit}
          >
            <Button
              type="submit"
              variant={Button.Variant.Primary}
              iconPosition={Button.IconPosition.Right}
              fullWidth={true}
              id="gameStepSubmitButton"
              onClick={() => {
                pushDatalayer(
                  `click_element_name`,
                  `Button - Weiter (Zusammenfassung)`,
                  DataLayerEvent.UserClick
                );
              }}
            >
              Weiter
              <Icon variant={IconVariant.ArrowRight} />
            </Button>
          </form>
          <SummaryPrice
            title={
              !externalGluecksSpirale ? 'Ihr Spieleinsatz:' : 'Spieleinsatz'
            }
            subtitle="zzgl. Bearbeitungsgebühr"
            price={props.gamePrice}
          />

          {MarketingParams.isCashback() && (
            <div>
              <div
                className={classnames(
                  'Cashback--tile',
                  `Cashback--tile-${MarketingParams.getNetwork()}`
                )}
              >
                <div>
                  <div>
                    <img
                      src={MarketingParams.getNetworkLogo('game-module')}
                      alt=""
                    />
                    <div className={classnames('Cashback--tile-text')}>
                      Beim Kauf sammeln Sie{' '}
                      <b>{MarketingParams.getRewardCurrency()}</b>. Die Abgabe
                      des Spielauftrags erfolgt über Ihre zuständige
                      Landeslotteriegesellschaft.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </StickyBox>
      </div>
    </div>
  );
};

export default GameStepSummary;
